import { z } from 'zod'

import { BridgeStatus } from '@/client'
import { pruned } from '@/lib/filters'

/** The PO list's page sizes. */
export const PAGE_SIZES = [25, 50, 100] as const
export const DEFAULT_PAGE_SIZE = 25

/** How far back the page opens: POs arrive continuously, so the last month. */
export const DEFAULT_WINDOW_DAYS = 30

/** The page's two views: Amazon vendor POs (the default) and our own retailer POs. */
export const VIEWS = ['amazon', 'retailer'] as const
export type PoFulfilmentView = (typeof VIEWS)[number]

/** Where a retailer PO stands, in the order a person would ask about it. */
export const RETAILER_STATUSES = [
  'not_started',
  'in_progress',
  'invoiced',
  'delivered',
  'expired',
] as const
export type RetailerStatus = (typeof RETAILER_STATUSES)[number]

const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)

/**
 * PO fulfilment's URL: the view, the marketplace, the PO dates, the status
 * filter, the page and its size, and the PO whose panel is open, so an open PO
 * is a link. Every field optional; the dates default to the last month at read
 * time and are never written into the URL for it.
 *
 * Marketplace and dates are shared by both views. The Amazon view's own are
 * `status`, `page`, `size`, `store` + `po`; the retailer view's are `fill`
 * (its status, a different set) and `open` (the PO id).
 */
export const poFulfilmentSearchSchema = z.object({
  view: z
    .enum(VIEWS)
    .optional()
    .catch(undefined)
    .describe('Which purchase orders are shown: Amazon vendor POs or our retailer POs.'),
  marketplaceId: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The marketplace the purchase orders belong to.'),
  from: day.describe('The first PO date of the window, YYYY-MM-DD; 30 days ago if absent.'),
  to: day.describe('The last PO date of the window, YYYY-MM-DD, inclusive; today if absent.'),
  status: z
    .enum(Object.values(BridgeStatus) as [BridgeStatus, ...BridgeStatus[]])
    .optional()
    .catch(undefined)
    .describe('The fulfilment status an Amazon vendor PO is narrowed to.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The page of the Amazon PO list, from 1.'),
  size: z
    .number()
    .refine((size) => (PAGE_SIZES as readonly number[]).includes(size))
    .optional()
    .catch(undefined)
    .describe('Rows per page of the Amazon PO list: 25, 50 or 100.'),
  store: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The store id of the expanded Amazon PO.'),
  po: z
    .string()
    .min(1)
    .max(16)
    .optional()
    .catch(undefined)
    .describe('The PO number of the expanded Amazon PO.'),
  fill: z
    .enum(RETAILER_STATUSES)
    .optional()
    .catch(undefined)
    .describe('How far a retailer PO has been filled.'),
  open: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The id of the expanded retailer PO.'),
})

export type PoFulfilmentSearch = z.infer<typeof poFulfilmentSearchSchema>

export const EMPTY_PO_FULFILMENT_SEARCH: PoFulfilmentSearch = poFulfilmentSearchSchema.parse({})

/** Strip defaults and empties so a clean URL stays clean. */
export function withoutPoFulfilmentDefaults(search: PoFulfilmentSearch): PoFulfilmentSearch {
  return pruned({
    ...search,
    page: search.page === 1 ? undefined : search.page,
    view: search.view === 'amazon' ? undefined : search.view,
    size: search.size === DEFAULT_PAGE_SIZE ? undefined : search.size,
  })
}

/**
 * The search change that moves to `view`: the marketplace and dates carry
 * over, and everything that belongs to one view is cleared, so a filter or an
 * open PO from one never narrows the other.
 */
export function toView(view: PoFulfilmentView): Partial<PoFulfilmentSearch> {
  return {
    view: view === 'amazon' ? undefined : view,
    status: undefined,
    page: undefined,
    size: undefined,
    store: undefined,
    po: undefined,
    fill: undefined,
    open: undefined,
  }
}

/** `YYYY-MM-DD` for `days` before `today`, in the browser's own calendar. */
export function isoDaysBefore(today: Date, days: number): string {
  const day = new Date(today)
  day.setDate(day.getDate() - days)
  const month = String(day.getMonth() + 1).padStart(2, '0')
  const date = String(day.getDate()).padStart(2, '0')
  return `${String(day.getFullYear())}-${month}-${date}`
}

/** The dates the page reads: the URL's, or the last month to today. */
export function windowOf(search: PoFulfilmentSearch, today: Date): { from: string; to: string } {
  const to = search.to ?? isoDaysBefore(today, 0)
  return { from: search.from ?? isoDaysBefore(today, DEFAULT_WINDOW_DAYS), to }
}

/** The instant after an inclusive `YYYY-MM-DD`, so the window is a half-open bound. */
export function nextDay(iso: string): string {
  const day = new Date(`${iso}T00:00:00.000Z`)
  day.setUTCDate(day.getUTCDate() + 1)
  return day.toISOString()
}
